// SEQ-180 phase 2, task P13: one rollup pass per request key, shared by every
// widget that reads that key.
//
// The phase 1 page did this once for its two calls (PnlAnalytics.tsx's
// projectRollups / monthRollups / companyTotals memos). A dashboard has a
// variable number of keys, so the same work is done here per key and handed
// to the widgets by key string. The arithmetic is untouched: the same
// `projectRowsToRollups`, `monthRowsToRollups` and `sumRollups` the phase 1
// page called, in the same order, which is what keeps the KPI cards' numbers
// identical.

import type { PnlAnalyticsRow } from "../../lib/pnl/analyticsClient";
import { resolveProjectName } from "./pnlAnalyticsFormat";
import {
  monthRowsToRollups,
  projectRowsToRollups,
  sumRollups,
  type MonthRollup,
  type PnlRollup,
  type ProjectRollup,
} from "./rollup";
import type { PnlRequestKey } from "./dashboardRequestKeys";

export type PnlDisplayProjectRollup = ProjectRollup & { projectName: string; status: string };

export interface PnlKeyView {
  rows: PnlAnalyticsRow[];
  projectRows: PnlDisplayProjectRollup[];
  monthRows: MonthRollup[];
  companyTotals: PnlRollup;
  /** False while this key's call is still in flight or has failed, so a widget can show a skeleton rather than a wrong zero. */
  loaded: boolean;
  /**
   * SEQ-193. The ISO `yyyy-MM-dd` range this key actually asked the RPC for
   * (the request plan's own resolved `from`/`to`; `null` for an unbounded
   * "project lifetime" range). A month_on_month widget needs the true
   * requested bounds, not just the months its rows happen to cover, so an
   * empty month at either EDGE of the range still renders as a blank column
   * rather than being silently dropped.
   */
  from: string | null;
  to: string | null;
}

export interface PnlProjectNameSource {
  id: string;
  job_location: string;
  status?: string | null;
}

export const EMPTY_KEY_VIEW: PnlKeyView = {
  rows: [],
  projectRows: [],
  monthRows: [],
  companyTotals: sumRollups([]),
  loaded: false,
  from: null,
  to: null,
};

export function buildKeyView(
  rows: PnlAnalyticsRow[] | undefined,
  projects: PnlProjectNameSource[],
  range: { from: string | null; to: string | null } = { from: null, to: null },
): PnlKeyView {
  if (!rows) return { ...EMPTY_KEY_VIEW, ...range };

  const projectsById = new Map(projects.map((p) => [p.id, p.job_location]));
  const statusById = new Map(projects.map((p) => [p.id, p.status ?? ""]));

  const groupLabelByProjectId = new Map<string, string>();
  for (const row of rows) {
    if (row.projectId && !groupLabelByProjectId.has(row.projectId)) {
      groupLabelByProjectId.set(row.projectId, row.groupLabel);
    }
  }

  const projectRollups = projectRowsToRollups(rows);
  const projectRows: PnlDisplayProjectRollup[] = projectRollups.map((p) => ({
    ...p,
    projectName: resolveProjectName(p.projectId, groupLabelByProjectId.get(p.projectId), projectsById),
    status: statusById.get(p.projectId) ?? "",
  }));

  return {
    rows,
    projectRows,
    monthRows: monthRowsToRollups(rows),
    companyTotals: sumRollups(projectRollups),
    loaded: true,
    from: range.from,
    to: range.to,
  };
}

/** One view per request key, so N widgets on one key share one rollup pass. */
export function buildKeyViews(
  keys: PnlRequestKey[],
  byKey: Record<string, { rows: PnlAnalyticsRow[] } | undefined>,
  projects: PnlProjectNameSource[],
): Record<string, PnlKeyView> {
  const views: Record<string, PnlKeyView> = {};
  for (const key of keys) {
    views[key.key] = buildKeyView(byKey[key.key]?.rows, projects, { from: key.from, to: key.to });
  }
  return views;
}
